import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextButton } from '@fiverr-private/button';
import { Container, Stack } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { I18n } from '@fiverr-private/i18n-react';
import { Link, Text } from '@fiverr-private/typography';
import { ArrowRightIcon, ChevronLeftIcon } from '@fiverr-private/visuals';
import { useNavigationBarReporter } from '../../../events/bianka/navigationBar';
import { CategoriesNavigationMetric, countCategoriesNavigation } from '../../../events/grafana/categoriesNavigation';
import type { ExtendedTransformedCmsConfigItem, ExtendedTransformedCmsConfigItemLevels } from './transformer/types';
import { getCategories, getCategoryGroups, hasLink } from './utils';
import { DRILL, PANEL_WIDTH } from './constants';
import { CategoryIcon } from './CategoryIcon';
import { CategoryRow } from './CategoryRow';
import { MenuRow } from './MenuRow';
import { SubcategoryGrid } from './SubcategoryGrid';
import { cardWidth, columnEnter, focusItem, listExit } from './CategoriesMenuPanel.ve.css';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

interface CategoriesMenuPanelProps {
    categories: ExtendedTransformedCmsConfigItemLevels;
}

export const CategoriesMenuPanel = ({ categories }: CategoriesMenuPanelProps) => {
    const sections = categories.level_1?.[0] ?? [];
    const [sectionId, setSectionId] = useState(sections[0]?.id);
    const [openedId, setOpenedId] = useState<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [isListExiting, setIsListExiting] = useState(false);
    const [isBodyScrolled, setIsBodyScrolled] = useState(false);
    const drillTimeout = useRef<ReturnType<typeof setTimeout>>();
    const lastOpenedId = useRef<string | null>(null);
    const hasNavigated = useRef(false);
    const columnRef = useRef<HTMLDivElement>(null);
    const { reportOption, reportExplore } = useNavigationBarReporter();

    const section = sections.find(({ id }) => id === sectionId) ?? sections[0];
    const sectionCategories = useMemo(() => (section ? getCategories(categories, section) : []), [categories, section]);
    const opened = sectionCategories.find(({ id }) => id === openedId);
    const groups = useMemo(() => (opened ? getCategoryGroups(categories, opened) : []), [categories, opened]);
    const columnWidth = opened ? PANEL_WIDTH.COLUMNS_OPENED : PANEL_WIDTH.COLUMNS_CLOSED;

    useEffect(() => () => clearTimeout(drillTimeout.current), []);

    useEffect(() => setIsBodyScrolled(false), [openedId, sectionId]);

    useEffect(() => {
        if (!hasNavigated.current) {
            return;
        }

        const target =
            (openedId === null && lastOpenedId.current
                ? columnRef.current?.querySelector<HTMLElement>(`[data-category-id="${lastOpenedId.current}"]`)
                : null) ?? columnRef.current?.querySelector<HTMLElement>('button, a');

        target?.focus({ preventScroll: true });
    }, [openedId]);

    if (!section) {
        return null;
    }

    const selectSection = (item: ExtendedTransformedCmsConfigItem) => {
        countCategoriesNavigation(CategoriesNavigationMetric.SECTION_CLICK);
        reportOption({ section: item });
        clearTimeout(drillTimeout.current);
        setSectionId(item.id);
        setOpenedId(null);
        setActiveId(null);
        setIsListExiting(false);
    };

    const open = (category: ExtendedTransformedCmsConfigItem) => {
        const { id } = category;
        countCategoriesNavigation(CategoriesNavigationMetric.CATEGORY_CLICK, category.reportData);
        reportOption({ section, category });
        clearTimeout(drillTimeout.current);
        setActiveId(id);

        const reduceMotion = prefersReducedMotion();
        setIsListExiting(!reduceMotion);
        drillTimeout.current = setTimeout(
            () => {
                hasNavigated.current = true;
                lastOpenedId.current = id;
                setIsListExiting(false);
                setOpenedId(id);
            },
            reduceMotion ? 0 : DRILL.LIST_EXIT_MS
        );
    };

    const goBack = () => {
        hasNavigated.current = true;
        setIsListExiting(false);
        setActiveId(null);
        setOpenedId(null);
    };

    return (
        <Stack
            className={cardWidth}
            gap="0"
            overflow="hidden"
            paddingLeft="4"
            width={`calc(${PANEL_WIDTH.CATEGORIES_LIST + columnWidth}px + ${theme.spacing[8]})`}
            height="670px"
        >
            <Stack
                as="nav"
                direction="column"
                gap="0"
                alignSelf="stretch"
                flexShrink="0"
                width={`${PANEL_WIDTH.CATEGORIES_LIST}px`}
                minHeight="1px"
                overflowY="auto"
                paddingX="3"
                paddingY="5"
                borderRightWidth="sm"
                borderStyle="solid"
                borderColor="grey_400"
            >
                {sections.map((item) => {
                    const { id, displayName, webIcon } = item;
                    const isSelected = id === section.id;

                    return (
                        <MenuRow key={id} isSelected={isSelected} onClick={() => selectSection(item)}>
                            <Container display="flex" flexShrink="0" color="grey_1200" aria-hidden="true">
                                <CategoryIcon webIcon={webIcon} />
                            </Container>
                            <Text
                                color="bodyPrimary"
                                fontWeight={isSelected ? 'semibold' : 'normal'}
                                overflowWrap="anywhere"
                            >
                                {displayName}
                            </Text>
                        </MenuRow>
                    );
                })}
            </Stack>
            <Stack ref={columnRef} direction="column" gap="0" flex="1 1 auto" minWidth="1px" height="100%">
                {opened ? (
                    <Stack key={opened.id} direction="column" gap="0" flex="1 1 auto" minHeight="1px">
                        <Stack
                            className={columnEnter}
                            alignItems="center"
                            justifyContent="spaceBetween"
                            flexShrink="0"
                            minHeight="54px"
                            paddingTop="5"
                            paddingBottom="2"
                            paddingLeft="4"
                            paddingRight="10"
                            borderBottomWidth="sm"
                            borderStyle="solid"
                            borderColor={isBodyScrolled ? 'grey_500' : 'transparent'}
                            transition="border-color 0.2s"
                            backgroundColor="white"
                            style={{ animationDelay: `${DRILL.COLUMNS_START_MS}ms` }}
                        >
                            <Container className={focusItem}>
                                <TextButton onClick={goBack}>
                                    <Stack alignItems="center" gap="1" paddingX="2" paddingY="1">
                                        <ChevronLeftIcon size="md" color="currentColor" />
                                        <Text size="b_lg" fontWeight="semibold" color="bodyPrimary">
                                            {opened.displayName}
                                        </Text>
                                    </Stack>
                                </TextButton>
                            </Container>
                            {hasLink(opened) && (
                                <Link
                                    className={focusItem}
                                    href={opened.url}
                                    onClick={() => {
                                        countCategoriesNavigation(
                                            CategoriesNavigationMetric.EXPLORE_CLICK,
                                            opened.reportData
                                        );
                                        reportExplore({ section, category: opened });
                                    }}
                                    size="b_xs"
                                    fontWeight="semibold"
                                    color="grey_1200"
                                    decoration={{ default: 'none', hover: 'underline' }}
                                    whiteSpace="nowrap"
                                >
                                    <Stack alignItems="center" gap="1" paddingX="2" paddingY="1">
                                        <I18n k="header.categories_navigation.explore_category" />
                                        <ArrowRightIcon size="xs" color="currentColor" />
                                    </Stack>
                                </Link>
                            )}
                        </Stack>
                        <Container
                            flex="1 1 auto"
                            minHeight="1px"
                            overflowY="auto"
                            paddingTop="3"
                            paddingBottom="4"
                            paddingLeft="4"
                            paddingRight="4"
                            onScroll={(event) => setIsBodyScrolled(event.currentTarget.scrollTop > 0)}
                        >
                            <SubcategoryGrid
                                groups={groups}
                                onLinkClick={(item, heading) => {
                                    countCategoriesNavigation(CategoriesNavigationMetric.LINK_CLICK, item.reportData);
                                    reportOption({
                                        section,
                                        category: opened,
                                        subCategory: heading ?? item,
                                        nestedSubCategory: heading ? item : undefined,
                                    });
                                }}
                                columnDelaysMs={[
                                    DRILL.COLUMNS_START_MS,
                                    DRILL.COLUMNS_START_MS + DRILL.COLUMN_STAGGER_MS,
                                ]}
                            />
                        </Container>
                    </Stack>
                ) : (
                    <Stack
                        key={section.id}
                        className={isListExiting ? listExit : undefined}
                        direction="column"
                        gap="0"
                        flex="1 1 auto"
                        minHeight="1px"
                        overflowY="auto"
                        paddingTop="5"
                        paddingBottom="7"
                        paddingLeft="4"
                        paddingRight="4"
                    >
                        {sectionCategories.map((category) => (
                            <CategoryRow
                                key={category.id}
                                id={category.id}
                                label={category.displayName}
                                webIcon={category.webIcon}
                                isSelected={category.id === activeId}
                                onClick={() => open(category)}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
